// Module 8 — Recoveries.
// Route: /reinsurance/recoveries
//
// Collections, follow-up and settlement monitoring. The statuses are a
// collections vocabulary, not a claims lifecycle: what the desk needs at a
// glance is whether the money is late.

import React from 'react';
import { useTranslation } from 'react-i18next';
import ModulePage from './ModulePage';
import RecoveryDrawer from './RecoveryDrawer';
import {
  C, Icons, Kpi, Pill, PrimaryButton, GhostButton,   money0, moneyMixed, } from './kit';

// Aging in days, coloured by how bad it is. 0d in green is a deliberate
// statement — it means nothing is outstanding, which is good news, and
// rendering it in the same grey as "unknown" would hide that.
function Aging({ days, outstanding }) {
  if (!Number(outstanding)) return <span style={{ color: C.green, fontWeight: 600 }}>0d</span>;
  const d = Number(days) || 0;
  const colour = d > 90 ? C.red : d > 30 ? C.amber : C.green;
  return <span style={{ color: colour, fontWeight: 700 }}>{d}d</span>;
}

// Raising a recovery.
//
// The recoverable is worked out here rather than typed: gross loss, less the
// cedant's retention, times the reinsurer's signed line. Typing the answer
// straight in is how a recovery ends up claiming a number the treaty does
// not support, and the market sends it back.
const recoveryForm = (t) => ({
  title: t('screens.reRecoveries.newTitle', 'New recovery'),
  subtitle: t('screens.reRecoveries.newSub', 'Claim back what the treaty owes'),
  submitLabel: t('screens.reRecoveries.createBtn', 'Raise recovery'),
  endpoint: '/reinsurance/recoveries',
  fields: [
    { key: 'cedantName', label: t('screens.reRecoveries.fCedantIn', 'Cedant'), required: true,
      placeholder: 'Riyadh Properties Co.' },
    { key: 'counterpartyName', label: t('screens.reRecoveries.fMarketIn', 'Counterparty (reinsurer)'),
      required: true, placeholder: 'Swiss Re' },
    { key: 'claimEvent', label: t('screens.reRecoveries.fEventIn', 'Claim / event'), required: true,
      placeholder: 'Property – Warehouse Fire' },
    { key: 'claimNumber', label: t('screens.reRecoveries.fClaimIn', 'Claim reference'),
      placeholder: 'GU-RE-CLM-2026-0004' },
    { key: 'treatyNumber', label: t('screens.reRecoveries.fTreatyIn', 'Treaty reference'),
      placeholder: 'TRY-2026-000002' },
    { key: 'grossLoss', label: t('screens.reRecoveries.fGrossIn', 'Gross loss'), type: 'number',
      required: true, placeholder: '28000000' },
    { key: 'retention', label: t('screens.reRecoveries.fRetentionIn', 'Cedant retention'), type: 'number',
      placeholder: '10000000',
      help: t('screens.reRecoveries.fRetentionHelp', 'Nothing below the attachment point is recoverable. Leave blank on proportional business.') },
    { key: 'sharePercent', label: t('screens.reRecoveries.fShareIn', 'Reinsurer signed line %'),
      type: 'number', required: true, placeholder: '35' },
    { key: 'currency', label: t('screens.reRecoveries.fCurrencyIn', 'Currency'), defaultValue: 'USD' },
    { key: 'dueDate', label: t('screens.reRecoveries.fDueIn', 'Due date'), type: 'date' },
  ],
  validate: (v) => {
    const gross = Number(v.grossLoss) || 0;
    const ret = Number(v.retention) || 0;
    if (ret >= gross && gross > 0) {
      return { retention: 'Retention is not less than the gross loss — nothing would be recoverable.' };
    }
    const share = Number(v.sharePercent) || 0;
    if (share <= 0 || share > 100) {
      return { sharePercent: 'A signed line is a percentage between 0 and 100.' };
    }
    return null;
  },
  toPayload: (v) => {
    const gross = Number(v.grossLoss) || 0;
    const retention = Number(v.retention) || 0;
    const share = Number(v.sharePercent) || 0;
    const aboveRetention = Math.max(0, gross - retention);
    const recoverable = Math.round(aboveRetention * (share / 100));
    return {
      cedantName: v.cedantName.trim(),
      counterpartyName: v.counterpartyName.trim(),
      claimEvent: v.claimEvent.trim(),
      ...(v.claimNumber ? { claimNumber: v.claimNumber.trim() } : {}),
      ...(v.treatyNumber ? { treatyNumber: v.treatyNumber.trim() } : {}),
      grossLoss: gross,
      cededPercent: gross > 0 ? Math.round((recoverable / gross) * 10000) / 100 : 0,
      recoverable,
      recovered: 0,
      outstanding: recoverable,
      currency: (v.currency || 'USD').trim().toUpperCase(),
      ...(v.dueDate ? { dueDate: v.dueDate } : {}),
      status: 'pending',
      // The workings, kept with the figure. A recoverable nobody can explain
      // is a recoverable nobody will agree to pay.
      calculation: {
        basis: retention > 0 ? 'excess_of_loss' : 'quota_share',
        retention: retention || null,
        sharePercent: share,
        computedRecoverable: recoverable,
        overridden: false,
        steps: [
          { label: 'Gross loss', amount: gross },
          ...(retention > 0 ? [
            { label: 'Less retention (attachment point)', amount: -retention,
              note: 'nothing below the attachment point is recoverable' },
            { label: 'Loss above attachment', amount: aboveRetention },
          ] : []),
          { label: `Reinsurer signed line ${share}%`, amount: recoverable },
        ],
      },
    };
  },
});

export default function RecoveriesList() {
  const { t } = useTranslation();

  const columns = [
    { key: 'recoveryNumber', label: t('screens.reRecoveries.colNumber', 'Recovery ref'), width: 150,
      render: (r) => <span style={{ fontWeight: 600, color: C.ink }}>{r.recoveryNumber}</span> },
    { key: 'cedantName', label: t('screens.reRecoveries.colCedant', 'Cedant'),
      render: (r) => r.cedantName || '—' },
    { key: 'counterpartyName', label: t('screens.reRecoveries.colCounterparty', 'Counterparty'),
      render: (r) => r.counterpartyName || '—' },
    { key: 'claimEvent', label: t('screens.reRecoveries.colClaim', 'Claim / event'),
      render: (r) => r.claimEvent || r.claimNumber || '—' },
    { key: 'recoverable', label: t('screens.reRecoveries.colRecoverable', 'Recoverable amount'), numeric: true,
      render: (r) => money0(r.recoverable, r.currency) },
    { key: 'recovered', label: t('screens.reRecoveries.colPaid', 'Paid amount'), numeric: true,
      render: (r) => money0(r.recovered, r.currency) },
    { key: 'outstanding', label: t('screens.reRecoveries.colBalance', 'Balance'), numeric: true,
      render: (r) => money0(r.outstanding, r.currency) },
    { key: 'aging', label: t('screens.reRecoveries.colAging', 'Aging'), numeric: true, width: 74,
      render: (r) => <Aging days={r.ageDays} outstanding={r.outstanding} /> },
    { key: 'assignedToName', label: t('screens.reRecoveries.colAssigned', 'Assigned to'),
      render: (r) => r.assignedToName || <span style={{ color: C.faint }}>unassigned</span> },
    { key: 'status', label: t('screens.reRecoveries.colStatus', 'Status'), width: 104,
      render: (r) => <Pill status={r.status} /> },
  ];

  return (
    <ModulePage
      title={t('screens.reRecoveries.title', 'Recoveries')}
      subtitle={t('screens.reRecoveries.subtitle', 'Collections, follow-up and settlement monitoring')}
      endpoint='/reinsurance/recoveries'
      searchPlaceholder={t('screens.reRecoveries.search', 'Search recoveries, cedants, counterparties…')}
      createForm={recoveryForm(t)}
      actions={({ openCreate }) => (
        <>
          <GhostButton icon={Icons.send()}>{t('screens.reRecoveries.remind', 'Send reminder')}</GhostButton>
          <PrimaryButton onClick={openCreate}>{t('screens.reRecoveries.new', 'New recovery')}</PrimaryButton>
        </>
      )}
      renderKpis={(s) => (
        <>
          <Kpi icon={Icons.db()} tone='blue'
            value={moneyMixed(s?.totalOutstanding)} trend={s?.totalOutstanding?.trend}
            comparison={s?.totalOutstanding?.comparison}
            label={t('screens.reRecoveries.kpiOutstanding', 'Total recoveries outstanding')} />
          <Kpi icon={Icons.cal()} tone='green'
            value={moneyMixed(s?.dueThisMonth)} trend={s?.dueThisMonth?.trend}
            comparison={s?.dueThisMonth?.comparison}
            label={t('screens.reRecoveries.kpiDue', 'Due this month')} />
          <Kpi icon={Icons.check()} tone='blue'
            value={moneyMixed(s?.collectedQuarter)} trend={s?.collectedQuarter?.trend}
            comparison={s?.collectedQuarter?.comparison}
            label={t('screens.reRecoveries.kpiCollected', 'Collected this quarter')} />
          <Kpi icon={Icons.alert()} tone='red'
            value={moneyMixed(s?.disputedBalances)} trend={s?.disputedBalances?.trend}
            comparison={s?.disputedBalances?.comparison}
            label={t('screens.reRecoveries.kpiDisputed', 'Disputed balances')} />
        </>
      )}
      filters={[
        { key: 'period', type: 'daterange', label: 'Due' },
        { key: 'status', label: 'Status', options: [
          { value: 'all', label: 'All statuses' },
          { value: 'pending', label: 'Pending' },
          { value: 'in_progress', label: 'In Progress' },
          { value: 'overdue', label: 'Overdue' },
          { value: 'settled', label: 'Settled' },
          { value: 'disputed', label: 'Disputed' },
          { value: 'written_off', label: 'Written off' },
        ] },
        { key: 'ageBand', label: 'Age', options: [
          { value: 'all', label: 'All ages' },
          { value: '0-30', label: '0–30 days' },
          { value: '31-60', label: '31–60 days' },
          { value: '61-90', label: '61–90 days' },
          { value: '91-180', label: '91–180 days' },
          { value: '180+', label: 'Over 180 days' },
        ] },
      ]}
      columns={columns}
      emptyMessage={t('screens.reRecoveries.empty', 'No recoveries. Raise one from a reinsurance claim to calculate the ceded portion.')}
      drawerTabs={['Overview', 'Payment History', 'Documents', 'Activity']}
      drawerHeader={(r) => ({
        title: r.recoveryNumber,
        badge: <Pill status={r.status} />,
        lines: [r.cedantName, r.claimEvent || r.claimNumber].filter(Boolean),
      })}
      drawer={(r, { tab, reload }) => (
        <RecoveryDrawer row={r} tab={tab} reload={reload} />
      )}
    />
  );
}
